import { styled } from "goober";
import { useMemo, useState } from "react";
import type { DailyRequestVolume } from "../../lib/connectorUsage";
import { Text } from "../Text";
import { MONITORING_CHART_HEIGHT } from "./monitoringPanelLayout";
import { InfoCorner, PanelBadge } from "./PanelInfo";
import { Panel, PanelHeader, TotalRow } from "./workflowsLayout";

export type RequestVolumeSeries = { key: string; label: string; color: string };

const Columns = styled("div")`
  display: flex;
  align-items: stretch;
  gap: 2px;
  width: 100%;
  min-width: 0;
  height: ${MONITORING_CHART_HEIGHT}px;
`;

const Column = styled("div")`
  flex: 1 1 0;
  min-width: 1px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  transition: opacity 120ms ease;
`;

const Segment = styled("div")`
  width: 100%;
  transition: height 120ms ease;
`;

const LabelRow = styled("div")`
  display: flex;
  gap: 2px;
  width: 100%;
  min-width: 0;
  height: 14px;
`;

const LabelCell = styled("div")`
  flex: 1 1 0;
  min-width: 1px;
  position: relative;
`;

const LabelText = styled("div")`
  position: absolute;
  left: 0;
  top: 0;
  white-space: nowrap;
`;

const Legend = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  min-width: 0;
`;

const LegendItem = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const LegendDot = styled("div")`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

function formatDayLabel(day: number): string {
  return new Date(day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function labelStep(dayCount: number): number {
  if (dayCount <= 10) return 2;
  if (dayCount <= 40) return 7;
  return 14;
}

export function ConnectorRequestVolumePanel({
  volume,
  series,
  seriesKeyOf,
  windowLabel,
}: {
  volume: readonly DailyRequestVolume[];
  series: readonly RequestVolumeSeries[];
  seriesKeyOf: ReadonlyMap<string, string>;
  windowLabel: string;
}) {
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  const stacked = useMemo(
    () =>
      volume.map((day) => {
        const bySeries = new Map<string, number>();
        for (const [connectorId, requests] of day.byConnector) {
          const key = seriesKeyOf.get(connectorId) ?? connectorId;
          bySeries.set(key, (bySeries.get(key) ?? 0) + requests);
        }
        return { day: day.day, total: day.total, errors: day.errors, bySeries };
      }),
    [volume, seriesKeyOf]
  );

  const totalRequests = stacked.reduce((sum, day) => sum + day.total, 0);
  const totalErrors = stacked.reduce((sum, day) => sum + day.errors, 0);
  const maxTotal = Math.max(1, ...stacked.map((day) => day.total));
  const step = labelStep(stacked.length);
  const hovered = stacked.find((day) => day.day === hoveredDay) ?? null;

  // The columns stack bottom-up, so render the ranked series in reverse to keep
  // the largest connector anchored to the baseline; the legend stays ranked.
  const stackOrder = useMemo(() => [...series].reverse(), [series]);

  const caption = hovered
    ? `${formatDayLabel(hovered.day)} · ${hovered.total.toLocaleString("en-US")} ${hovered.total === 1 ? "request" : "requests"} · ${hovered.errors.toLocaleString("en-US")} ${hovered.errors === 1 ? "error" : "errors"}`
    : `${totalErrors.toLocaleString("en-US")} ${totalErrors === 1 ? "error" : "errors"}`;

  return (
    <Panel>
      <InfoCorner>
        <PanelBadge>Last {windowLabel}</PanelBadge>
      </InfoCorner>
      <PanelHeader>
        <Text variant="text-small-md">Request volume</Text>
        <TotalRow>
          <Text variant="display-md">
            {(hovered ? hovered.total : totalRequests).toLocaleString("en-US")}
          </Text>
          <Text variant="text-small" color="var(--text-secondary)">
            {caption}
          </Text>
        </TotalRow>
      </PanelHeader>
      <Columns onMouseLeave={() => setHoveredDay(null)}>
        {stacked.map((day) => {
          const topIndex = stackOrder.findIndex(
            (s) => (day.bySeries.get(s.key) ?? 0) > 0
          );
          return (
            <Column
              key={day.day}
              onMouseEnter={() => setHoveredDay(day.day)}
              style={{
                opacity: hoveredDay != null && hoveredDay !== day.day ? 0.4 : 1,
              }}
            >
              {stackOrder.map((s, i) => {
                const value = day.bySeries.get(s.key) ?? 0;
                return (
                  <Segment
                    key={s.key}
                    style={{
                      background: s.color,
                      height: `${(value / maxTotal) * 100}%`,
                      minHeight: value > 0 ? 2 : 0,
                      borderRadius: i === topIndex ? "2px 2px 0 0" : 0,
                    }}
                  />
                );
              })}
            </Column>
          );
        })}
      </Columns>
      <LabelRow>
        {stacked.map((day, i) => (
          <LabelCell key={day.day}>
            {i % step === 0 && (
              <LabelText>
                <Text variant="text-small" color="var(--text-secondary)">
                  {formatDayLabel(day.day)}
                </Text>
              </LabelText>
            )}
          </LabelCell>
        ))}
      </LabelRow>
      {series.length > 0 && (
        <Legend>
          {series.map((s) => (
            <LegendItem key={s.key}>
              <LegendDot style={{ background: s.color }} />
              <Text variant="text-small" color="var(--text-secondary)" ellipsis>
                {s.label}
              </Text>
            </LegendItem>
          ))}
        </Legend>
      )}
    </Panel>
  );
}
